import React, { useEffect, useCallback } from "react";
import CloseIcon from "../icons/CloseIcon";

type OverlayType = "transparent" | "dark";

interface ModalProps {
  width?: number | string;
  showClose?: boolean;
  body: React.ReactNode;
  overlay?: OverlayType;
  onClose: () => void;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  width = 800,
  showClose = false,
  body,
  overlay = "dark",
  onClose,
  closeOnBackdropClick = true,
  closeOnEscape = true,
}) => {
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === "Escape") {
        onClose();
      }
    },
    [onClose, closeOnEscape]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [handleEscape]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleOverlayKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && (event.key === "Enter" || event.key === " ") && event.target === event.currentTarget) {
      event.preventDefault();
      onClose();
    }
  };

  const overlayStyles: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backgroundColor: overlay === "dark" ? "rgba(0, 0, 0, 0.5)" : "transparent",
  };

  const modalStyles: React.CSSProperties = {
    position: "relative",
    width: typeof width === "number" ? `${width}px` : width,
    maxWidth: "calc(100vw - 48px)",
    maxHeight: "calc(100vh - 48px)",
    backgroundColor: "#FFF",
    borderRadius: "8px",
    border: "1px solid var(--color-turquoise, #24AEA8)",
    padding: "24px",
    boxShadow: "0 4px 24px 0 rgba(0, 0, 0, 0.15)",
    overflow: "auto",
  };

  const closeButtonStyles: React.CSSProperties = {
    position: "absolute",
    right: "24px",
    top: "24px",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div
      style={overlayStyles}
      onClick={handleOverlayClick}
      onKeyDown={handleOverlayKeyDown}
      role="button"
      tabIndex={0}
      aria-label="Close modal"
    >
      <div style={modalStyles}>
        {showClose && (
          <button style={closeButtonStyles} onClick={onClose} aria-label="Close modal">
            <CloseIcon color="#63615C" />
          </button>
        )}
        {React.isValidElement(body)
          ? React.cloneElement(body as React.ReactElement<{ onClose?: () => void }>, {
              onClose,
            })
          : body}
      </div>
    </div>
  );
};

export default Modal;
